import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { STAT_MENU, STAT_PRESETS, deriveActiveStatIds, formatHeight } from '../lib/playerSearchCatalog';
import VerifiedFlame from '../components/VerifiedFlame';
import { PageHeader, MonoLabel, EmptyState } from '../components/Editorial';
import Button from '../components/Button';
import { RailFrame, AdminWindowSwitcher, usesRail } from '../components/AdminShell';
import { useCatalog } from '../context/CatalogContext';
import SearchCard from '../components/search/SearchCard';
import SavedNeedsMenu from '../components/search/SavedNeedsMenu';
import { AthleteCard, AthleteTable } from '../components/search/AthleteCard';
import StarGrade from '../components/lists/StarGrade';
import '../components/search/Search.css';

// The four fixed need stages. Same on every board and every platform —
// mirrors NeedStage on iOS and STAGES in app/services/need_matching.py.
// (Replaced the old five-stage coach-list pipeline; new+interested collapsed
// into Watching and contacted+replied into Contacted when lists became needs.)
const PIPELINE_STAGES = [
  { value: 'watching',  label: 'Watching',  color: '#0066cc', bg: '#eff6ff' },
  { value: 'contacted', label: 'Contacted', color: '#5856d6', bg: '#f5f3ff' },
  { value: 'visiting',  label: 'Visiting',  color: '#16a34a', bg: '#f0fdf4' },
  { value: 'offered',   label: 'Offered',   color: '#f59e0b', bg: '#fffbeb' },
];

// Quick-filter chips above the member list. `stages` is the set of pipeline
// stages a member must be in to show; `sort` switches to recency sort.
const MEMBER_FILTERS = [
  { id: 'all',      label: 'All' },
  { id: 'followup', label: 'Needs follow-up', stages: ['watching'] },
  { id: 'contacted', label: 'Contacted',      stages: ['contacted'] },
  { id: 'visiting', label: 'Visiting',        stages: ['visiting'] },
  { id: 'offered',  label: 'Offered',         stages: ['offered'] },
  { id: 'recent',   label: 'Recently added',  sort: 'recent' },
];

// Unseen-activity mark (W7). DESIGN: the meeting said "blue dot," but David
// flagged that this app already uses blue for "verified" (VerifiedFlame,
// .verified-dot) — a blue mark here would read as "verified" rather than
// "new." Green was tried next and rejected too: --color-primary already
// means "completion / done" in this design system, so a green mark reads
// as the inverse of "something new here." Current placeholder is
// --color-accent-gold (neither blue nor green) — see .new-activity-dot in
// Layout.css. iOS's NewActivityDot (CoachRecruitPrimitives.swift) uses the
// matching colour; the two must change together, since a placeholder that
// differs per platform can't be reviewed as one thing. Behaviour (when it
// shows) is fixed; the treatment is still David's decision and is kept in
// this one component so swapping it later is a one-file change. Do not
// scatter colour or sizing across call sites — always render this.
function NewActivityDot() {
  return (
    <span className="new-activity-dot" role="img" aria-label="New activity" title="New activity" />
  );
}

export default function CoachPlayerSearchPage() {
  const { user, API_URL } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    graduation_year: '', position: '', state: '', sport: '',
    gpa_weighted_min: '', gpa_weighted_max: '',
    gpa_unweighted_min: '', gpa_unweighted_max: '',
    act_min: '', act_max: '',
    sat_min: '', sat_max: '',
    height_min: '', height_max: '',
    weight_min: '', weight_max: '',
    wingspan_min: '', wingspan_max: '',
    hand_size_min: '', hand_size_max: '',
    dash_40_min: '', dash_40_max: '',
    run_10_min: '', run_10_max: '',
    run_20_min: '', run_20_max: '',
    vertical_jump_min: '', vertical_jump_max: '',
    short_shuttle_min: '', short_shuttle_max: '',
    three_cone_min: '', three_cone_max: '',
    income_min: '', income_max: '',
    power_score_min: '', power_score_max: '',
    min_coachability: '',
    smtq_band_min: '',
    hobby: '',
    college_region: '',
    division: '',
    campus_setting: '',
    school_size: '',
    school_type: '',
    verified_only: false,
    has_photo: false,
    camp_id: '',
  });
  const [players, setPlayers] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [coachLists, setCoachLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState(null);
  const [listPlayers, setListPlayers] = useState([]);
  const [memberFilter, setMemberFilter] = useState('all');
  const [compareIds, setCompareIds] = useState(new Set());
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [hoveredGroup, setHoveredGroup] = useState(null);
  const [activeStatIds, setActiveStatIds] = useState([]);
  const filterMenuRef = useRef(null);
  // Saved searches
  const [savedSearches, setSavedSearches] = useState([]);
  const [savedSearchMenuId, setSavedSearchMenuId] = useState(null);

  // Message List modal state
  const [showMessageListModal, setShowMessageListModal] = useState(false);
  const [bulkMessageText, setBulkMessageText] = useState('');
  const [bulkAttachmentFile, setBulkAttachmentFile] = useState(null);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkSendResult, setBulkSendResult] = useState(null);
  const [bulkConfirmStep, setBulkConfirmStep] = useState(false);

  // Admin / staff / partner get the search card + results layout inside the rail; coaches keep the
  // original page. The Cards | Table choice is remembered per browser.
  const railView = usesRail(user?.role);
  const { positionsFor, sportSlugFor } = useCatalog();
  // Positions for the sport being searched (football until a sport filter is set).
  const catalogPositions = positionsFor(sportSlugFor(filters.sport)).map((p) => p.label);
  const [view, setView] = useState(() => {
    // Table is the default; only an explicit saved 'cards' choice brings the cards back.
    try { return localStorage.getItem('pz.search.view') === 'cards' ? 'cards' : 'table'; } catch { return 'table'; }
  });
  const changeView = (v) => {
    setView(v);
    try { localStorage.setItem('pz.search.view', v); } catch { /* storage unavailable: the choice just won't persist */ }
  };

  const getAxiosConfig = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => { fetchLists(); fetchSavedSearches(); }, []);

  const [campOptions, setCampOptions] = useState([]);
  useEffect(() => {
    axios.get(`${API_URL}/camps?include_past=true`, getAxiosConfig())
      .then((res) => setCampOptions(res.data || []))
      .catch(() => setCampOptions([]));
  }, []); // load once on mount

  // Saved searches and lists collapsed into one noun: a NEED. One fetch now
  // feeds both sidebar panels — the criteria list and the saved athletes.
  const fetchSavedSearches = async () => {
    try {
      const res = await axios.get(`${API_URL}/coach-needs/`, getAxiosConfig());
      const needs = res.data.needs || [];
      setSavedSearches(needs);
      setCoachLists(needs);
      if (needs.length > 0 && !selectedListId) {
        setSelectedListId(needs[0].id);
        fetchListPlayers(needs[0].id);
      }
    } catch (e) { console.error('Error fetching needs:', e); }
  };

  // Build the JSON payload we persist for a saved search. Keep
  // searchTerm and verified_only alongside the rest of the filters so
  // a single object captures the entire search state.
  const buildFiltersPayload = () => ({ searchTerm, ...filters });

  // Web filters -> need criteria. A need models a deliberately narrower set
  // than this page can express (no free-text, camp, college-preference or
  // has-photo axis), so `unmappable` names what a save would drop and the
  // caller warns rather than silently losing it.
  const NEED_UNMAPPABLE = [
    ['searchTerm', 'name search'], ['camp_id', 'camp'], ['has_photo', 'has photo'],
    ['college_region', 'college region'], ['division', 'division'],
    ['campus_setting', 'campus setting'], ['school_size', 'school size'],
    ['school_type', 'school type'], ['min_coachability', 'coachability'],
    ['smtq_band_min', 'mental toughness'],
    ['hobby', 'hobby'], ['income_min', 'income'], ['income_max', 'income'],
    ['power_score_min', 'power score'], ['power_score_max', 'power score'],
  ];

  const buildNeedPayload = (name) => {
    const f = filters || {};
    const minimums = {};
    // *_min / *_max filter keys become need minimums. `_max` means
    // lower-is-better (a 40 time you must beat).
    Object.entries(f).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined || value === false) return;
      const isMin = key.endsWith('_min');
      const isMax = key.endsWith('_max');
      if (!isMin && !isMax) return;
      const base = key.replace(/_(min|max)$/, '');
      if (['income', 'power_score'].includes(base)) return;   // not need criteria
      const num = Number(value);
      if (Number.isNaN(num)) return;
      minimums[base] = { op: isMax ? 'lte' : 'gte', value: num };
    });

    return {
      name,
      sport_slugs: f.sport ? [f.sport] : [],
      sport_match_mode: 'any',
      positions: f.position ? [f.position] : [],
      position_match_mode: 'any',
      class_years: f.graduation_year ? [Number(f.graduation_year)] : [],
      states: f.state ? [f.state] : [],
      minimums,
      verified_only: !!f.verified_only,
    };
  };

  const droppedByNeed = () => {
    const f = { searchTerm, ...(filters || {}) };
    const seen = new Set();
    return NEED_UNMAPPABLE
      .filter(([key]) => f[key] !== '' && f[key] !== undefined && f[key] !== null && f[key] !== false)
      .map(([, label]) => label)
      .filter(label => (seen.has(label) ? false : seen.add(label)));
  };

  const applySavedSearch = (s) => {
    // A need carries structured criteria, not the old free-form filter blob.
    const rest = {
      sport: (s.sport_slugs || [])[0] || '',
      position: (s.positions || [])[0] || '',
      graduation_year: (s.class_years || [])[0] || '',
      state: (s.states || [])[0] || '',
      verified_only: !!s.verified_only,
    };
    Object.entries(s.minimums || {}).forEach(([key, spec]) => {
      if (!spec || spec.value === undefined) return;
      rest[spec.op === 'lte' ? `${key}_max` : `${key}_min`] = String(spec.value);
    });
    setSearchTerm('');
    setSelectedListId(s.id);
    // Reset every known filter key to its default, then overlay saved values
    // so removing a filter (since save) doesn't leak through.
    setFilters(prev => {
      const cleared = Object.fromEntries(
        Object.keys(prev).map(k => [k, (k === 'verified_only' || k === 'has_photo') ? false : ''])
      );
      const next = { ...cleared, ...rest };
      setActiveStatIds(deriveActiveStatIds(next));
      return next;
    });
    // Mark as recently used; refresh list ordering. Then run the search
    // (slight delay so the filter state has time to apply before the
    // params are read).
    setTimeout(() => searchPlayers(), 0);
  };

  const handleSaveCurrentSearch = async () => {
    const name = window.prompt('Name this saved search:');
    if (!name || !name.trim()) return;
    try {
      const dropped = droppedByNeed();
      if (dropped.length && !window.confirm(
        `A need can't hold these, so they won't be saved: ${dropped.join(', ')}.\n\nSave the rest?`
      )) return;
      await axios.post(`${API_URL}/coach-needs/`, buildNeedPayload(name.trim()), getAxiosConfig());
      fetchSavedSearches();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error saving search');
    }
  };

  const handleRenameSavedSearch = async (s) => {
    const next = window.prompt('Rename saved search:', s.name);
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === s.name) return;
    try {
      await axios.put(`${API_URL}/coach-needs/${s.id}`, { name: trimmed }, getAxiosConfig());
      fetchSavedSearches();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error renaming');
    }
  };

  const handleDeleteSavedSearch = async (s) => {
    if (!window.confirm(`Delete need "${s.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/coach-needs/${s.id}`, getAxiosConfig());
      fetchSavedSearches();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error deleting');
    }
  };

  // Lists and saved searches are the same objects now; one fetch serves both.
  const fetchLists = fetchSavedSearches;

  const fetchListPlayers = async (listId) => {
    if (!listId) return;
    try {
      const res = await axios.get(`${API_URL}/coach-needs/${listId}/board`, getAxiosConfig());
      // The board comes back grouped by stage; flatten to the flat row shape
      // this page already renders, carrying the stage onto each row.
      const stages = res.data.stages || {};
      const rows = Object.entries(stages).flatMap(([stage, members]) =>
        (members || []).map(m => ({ ...m, pipeline_stage: stage }))
      );
      setListPlayers(rows);
    } catch (e) { console.error('Error fetching list players:', e); }
  };

  useEffect(() => {
    if (selectedListId) fetchListPlayers(selectedListId);
  }, [selectedListId]);

  // Reset the quick-filter when switching lists.
  useEffect(() => { setMemberFilter('all'); }, [selectedListId]);

  // Update a member's pipeline stage. Optimistic local update; revert on error.
  // `playerId` is the coach-list membership row id (p.id), matching the
  // remove-from-list flow above.
  const updateMemberStage = async (playerId, nextStage) => {
    const theMember = listPlayers.find(p => p.id === playerId);
    const prevStage = theMember ? stageOf(theMember) : undefined;
    setListPlayers(rows => rows.map(p => p.id === playerId ? { ...p, pipeline_stage: nextStage } : p));
    try {
      // Keyed on the ATHLETE, not the membership row — an athlete can sit on
      // several needs and the stage is tracked per board.
      await axios.put(
        `${API_URL}/coach-needs/${selectedListId}/board/${theMember.athlete_user_id}`,
        { stage: nextStage },
        getAxiosConfig()
      );
    } catch (e) {
      console.error('Pipeline stage update error:', e);
      setListPlayers(rows => rows.map(p => p.id === playerId ? { ...p, pipeline_stage: prevStage } : p)); // revert only this row
    }
  };

  const SEARCH_PAGE_SIZE = 50;

  const searchPlayers = async ({ reset = true } = {}) => {
    // Coach search now requires at least one filter (anti-scrape — no
    // whole-roster browse). Skip the request and let the empty state prompt
    // the coach to refine instead of firing a request the backend will reject.
    if (!hasActiveFilters) {
      setPlayers([]);
      setTotal(0);
      setOffset(0);
      return;
    }
    setLoading(true);
    try {
      const nextOffset = reset ? 0 : offset;
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      Object.entries(filters).forEach(([k, v]) => {
        if (k === 'verified_only' || k === 'has_photo') { if (v) params.append(k, 'true'); }
        else if (v) params.append(k, v);
      });
      params.append('offset', nextOffset);
      params.append('limit', SEARCH_PAGE_SIZE);
      const res = await axios.get(`${API_URL}/athletes/search?${params.toString()}`, getAxiosConfig());
      const page = res.data.players || [];
      setPlayers(prev => (reset ? page : [...prev, ...page]));
      setTotal(res.data.total || 0);
      setOffset(nextOffset + SEARCH_PAGE_SIZE);
    } catch (e) { console.error('Search error:', e); }
    setLoading(false);
  };

  // Navigate to the canonical athlete-detail page rather than opening an
  // in-page modal (the old detail modal is retired — see /athletes/:userId).
  // "Seen" is keyed on (coach, athlete), not per list, so opening any
  // athlete's profile clears their unseen-activity mark everywhere this
  // coach sees them. Fire-and-forget: navigate happens first and
  // unconditionally, so a failed clear never blocks the coach from reaching
  // the profile they asked for. There is deliberately no .then() here to
  // refresh local state — navigate() unmounts this page before the POST
  // could resolve, so any setState in a callback would be a silent no-op
  // on an unmounted tree. The mark actually clears when the coach comes
  // back to this page and it remounts, via the fetchLists()/fetchSavedSearches()
  // call in the mount-time useEffect (~line 295), which re-reads
  // has_new_activity fresh from the server.
  const viewPlayer = (userId) => {
    navigate(`/athletes/${userId}`);
    if (!userId) return;
    axios.post(`${API_URL}/coach-needs/athlete/${userId}/seen`, {}, getAxiosConfig())
      .catch((e) => console.error('Error clearing unseen activity:', e));
  };

  const toggleCompare = (athleteUserId) => {
    setCompareIds(prev => {
      const next = new Set(prev);
      if (next.has(athleteUserId)) next.delete(athleteUserId);
      else next.add(athleteUserId);
      return next;
    });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters(prev => {
      const cleared = {};
      Object.keys(prev).forEach(k => { cleared[k] = (k === 'verified_only' || k === 'has_photo') ? false : ''; });
      return cleared;
    });
    setActiveStatIds([]);
    setPlayers([]);
    setTotal(0);
    setOffset(0);
  };

  // Close filter menu on click outside
  useEffect(() => {
    if (!filterMenuOpen) return;
    const handleClickOutside = (e) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(e.target)) {
        setFilterMenuOpen(false);
        setHoveredGroup(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [filterMenuOpen]);

  // Lookup helper: find stat def by id
  const findStatDef = (statId) => {
    for (const g of STAT_MENU) {
      const s = g.stats.find(s => s.id === statId);
      if (s) return { ...s, group: g.group };
    }
    return null;
  };

  const addStat = (statId) => {
    if (!activeStatIds.includes(statId)) {
      setActiveStatIds(prev => [...prev, statId]);
    }
    setFilterMenuOpen(false);
    setHoveredGroup(null);
  };

  const removeStat = (statId) => {
    const def = findStatDef(statId);
    if (!def) return;
    setActiveStatIds(prev => prev.filter(id => id !== statId));
    setFilters(prev => {
      const next = { ...prev };
      if (def.type === 'range') {
        next[def.minKey] = '';
        next[def.maxKey] = '';
      } else {
        next[def.filterKey] = '';
      }
      return next;
    });
  };

  const hasActiveFilters = searchTerm || Object.entries(filters).some(([k, v]) => (k === 'verified_only' || k === 'has_photo') ? v : v !== '');

  const stageOf = (p) => p.pipeline_stage || 'watching';

  // Funnel counts recomputed locally from the loaded members so the strip
  // updates instantly after a stage change (no round-trip).
  const pipelineCounts = listPlayers.reduce((acc, p) => {
    const s = stageOf(p);
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  // Apply the active quick-filter, then sort. "Recently added" is a recency
  // sort over all members; the others filter by pipeline stage. Default is the
  // grade-descending sort used previously.
  const activeFilterDef = MEMBER_FILTERS.find(f => f.id === memberFilter) || MEMBER_FILTERS[0];
  const sortedListPlayers = (() => {
    let rows = [...listPlayers];
    if (activeFilterDef.stages) {
      rows = rows.filter(p => activeFilterDef.stages.includes(stageOf(p)));
    }
    if (activeFilterDef.sort === 'recent') {
      return rows.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
    }
    // Manual drag-order (set via the iOS .onMove reorder endpoint) wins once
    // any member has a sort_order — rows already arrive in server order
    // (sort_order ASC NULLS LAST, created_at DESC), so leave them as-is.
    // Only fall back to the grade-desc/name-asc sort when every member is
    // still unordered (sort_order all null/undefined).
    const hasManualOrder = rows.some((p) => p.sort_order !== null && p.sort_order !== undefined);
    if (!hasManualOrder) {
      return rows.sort((a, b) => {
        const gradeA = a.grade || 0;
        const gradeB = b.grade || 0;
        if (gradeB !== gradeA) return gradeB - gradeA;
        return (a.name || '').localeCompare(b.name || '');
      });
    }
    return rows;
  })();


  // Search as you type for the rail layout: filters and the text box re-run the search after a pause.
  // (searchPlayers itself skips the request while no filter is set.)
  useEffect(() => {
    if (!railView) return undefined;
    const t = setTimeout(() => { searchPlayers(); }, 350);
    return () => clearTimeout(t);
  }, [railView, searchTerm, filters]);

  if (railView) {
    const subtitle = total > 0 || (hasActiveFilters && !loading)
      ? `${total} athlete${total === 1 ? '' : 's'} match your filters`
      : 'Add a filter to start';
    return (
      <RailFrame active="search">
        <header className="adm-header">
          <div>
            <h1 className="adm-header__title">Search</h1>
            <p className="adm-header__sub">{subtitle}</p>
          </div>
          <div className="srch-actions">
            {compareIds.size >= 2 && (
              <button type="button" className="adm-btn adm-btn--dark" onClick={() => navigate(`/compare?ids=${[...compareIds].join(',')}`)}>
                {`Compare (${compareIds.size})`}
              </button>
            )}
            <SavedNeedsMenu needs={savedSearches} onApply={applySavedSearch}
              onRename={handleRenameSavedSearch} onDelete={handleDeleteSavedSearch} />
            <AdminWindowSwitcher label="View" value={view} onChange={changeView}
              options={[{ value: 'cards', label: 'Cards' }, { value: 'table', label: 'Table' }]} />
          </div>
        </header>

        <SearchCard
          searchTerm={searchTerm} onSearchTerm={setSearchTerm} onSubmit={() => searchPlayers()}
          filters={filters} setFilters={setFilters} positions={catalogPositions}
          activeStatIds={activeStatIds} onAddStat={addStat} onRemoveStat={removeStat}
          campOptions={campOptions} hasActiveFilters={!!hasActiveFilters}
          onSaveNeed={handleSaveCurrentSearch} onClear={clearFilters} />

        <section className="srch-results" aria-label="Results">
          {players.length === 0 ? (
            <div className="adm__card">
              {loading ? <p className="adm-loading">Searching…</p>
                : hasActiveFilters ? <p className="adm-empty">No athletes found. Try fewer filters or a broader name.</p>
                : <p className="adm-empty">Search for athletes: enter a name or add a filter (class, position, state…) to begin.</p>}
            </div>
          ) : view === 'table' ? (
            <AthleteTable players={players} onOpen={viewPlayer} selected={compareIds} onToggle={toggleCompare} />
          ) : (
            <div className="srch-cards">
              {players.map((pl) => <AthleteCard key={pl.id} player={pl} onOpen={viewPlayer} />)}
            </div>
          )}
          {players.length > 0 && players.length < total && (
            <div className="adm-pager">
              <button type="button" className="adm-btn" disabled={loading} onClick={() => searchPlayers({ reset: false })}>
                {loading ? 'Loading…' : `Load more (${players.length} of ${total})`}
              </button>
            </div>
          )}
        </section>
      </RailFrame>
    );
  }

  return (
    <div className="coach-layout">
      {/* Sidebar — Needs (criteria) + the selected need's saved athletes */}
      <aside className="coach-sidebar">
        {/* Needs — click one to load its criteria into the search */}
        <div className="sidebar-header">
          <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>Needs</h3>
          <Button
            iconOnly
            variant="ghost"
            size="sm"
            onClick={handleSaveCurrentSearch}
            disabled={!hasActiveFilters}
            title={hasActiveFilters ? 'Save current filters as a need' : 'Add filters first'}
          >+</Button>
        </div>

        {savedSearches.length === 0 ? (
          <div style={{ padding: '12px 16px', fontSize: '12px', color: '#94a3b8' }}>
            No needs yet. Build a search and tap + to save it as one.
          </div>
        ) : (
          savedSearches.map(s => (
            <div
              key={s.id}
              className="sidebar-athlete-item"
              onClick={() => applySavedSearch(s)}
              style={{ cursor: 'pointer', position: 'relative' }}
              title="Load this need's criteria"
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                  <span style={{ fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flex: '0 1 auto' }}>
                    {s.name}
                  </span>
                  {s.has_new_activity && <NewActivityDot />}
                </div>
                <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                  {[
                    (s.sport_slugs || []).length && 'sport',
                    (s.positions || []).length && 'position',
                    (s.class_years || []).length && 'class',
                    (s.states || []).length && 'state',
                    Object.keys(s.minimums || {}).length && 'minimums',
                    s.verified_only && 'verified',
                  ].filter(Boolean).length} criteria · {s.board_count ?? 0} saved
                </div>
              </div>
              <Button
                iconOnly
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); setSavedSearchMenuId(savedSearchMenuId === s.id ? null : s.id); }}
                style={{ flexShrink: 0 }}
                aria-label="Need options"
              >⋯</Button>
              {savedSearchMenuId === s.id && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute', right: 8, top: '100%',
                    background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 50,
                    minWidth: '120px',
                  }}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    fullWidth
                    onClick={() => { setSavedSearchMenuId(null); handleRenameSavedSearch(s); }}
                    style={{ justifyContent: 'flex-start' }}
                  >Rename</Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    fullWidth
                    onClick={() => { setSavedSearchMenuId(null); handleDeleteSavedSearch(s); }}
                    style={{ justifyContent: 'flex-start' }}
                  >Delete</Button>
                </div>
              )}
            </div>
          ))
        )}

        <div style={{ height: '1px', background: '#e2e8f0', margin: '12px 0' }} />

        {/* Lists */}
        <div className="sidebar-header">
          <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>Lists</h3>
          <Button
            iconOnly
            variant="ghost"
            size="sm"
            onClick={() => {
              const name = window.prompt('Enter a name for your new list:');
              if (name && name.trim()) {
                axios.post(`${API_URL}/coach-needs/`, { name: name.trim() }, getAxiosConfig())
                  .then(() => fetchLists())
                  .catch(e => alert(e.response?.data?.detail || 'Error creating need'));
              }
            }}
            title="Create a new need"
          >+</Button>
        </div>

        {coachLists.length > 0 && (
          <div style={{ padding: '8px 12px' }}>
            <select
              className="filter-select"
              value={selectedListId || ''}
              onChange={(e) => setSelectedListId(Number(e.target.value))}
              style={{ width: '100%', fontSize: '12px', padding: '4px 8px', marginBottom: 0 }}
              aria-label="Select need"
            >
              {coachLists.map(l => <option key={l.id} value={l.id}>{l.name} ({l.board_count ?? 0})</option>)}
            </select>
          </div>
        )}

        {coachLists.length === 0 && (
          <div style={{ padding: '16px', fontSize: '13px', color: '#94a3b8', textAlign: 'center' }}>
            No needs yet. Click + to create one.
          </div>
        )}

        {/* Pipeline funnel strip — counts recomputed locally from members */}
        {selectedListId && listPlayers.length > 0 && (
          <div style={{ padding: '8px 12px 0' }}>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'stretch' }}>
              {PIPELINE_STAGES.map(stg => {
                const count = pipelineCounts[stg.value] || 0;
                return (
                  <div key={stg.value} title={`${stg.label}: ${count}`} style={{
                    flex: 1, minWidth: 0, textAlign: 'center',
                    padding: '4px 2px', borderRadius: '6px',
                    background: count > 0 ? stg.bg : '#f8fafc',
                    border: `1px solid ${count > 0 ? stg.color : '#e2e8f0'}`,
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: count > 0 ? stg.color : '#cbd5e1', lineHeight: 1.1 }}>{count}</div>
                    <div style={{ fontSize: '9px', fontWeight: 600, color: count > 0 ? stg.color : '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{stg.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Quick-filter chips */}
        {selectedListId && listPlayers.length > 0 && (
          <div style={{ padding: '8px 12px 4px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {MEMBER_FILTERS.map(f => {
              const active = memberFilter === f.id;
              return (
                <button
                  key={f.id}
                  onClick={() => setMemberFilter(f.id)}
                  style={{
                    padding: '3px 10px', fontSize: '11px',
                    fontWeight: active ? 700 : 500,
                    border: `1.5px solid ${active ? '#4D8064' : '#e2e8f0'}`,
                    borderRadius: '12px',
                    background: active ? '#ecfdf5' : 'transparent',
                    color: active ? '#4D8064' : '#64748b',
                    cursor: 'pointer', lineHeight: 1.4,
                  }}
                  aria-pressed={active}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        )}

        {sortedListPlayers.map(p => (
          <div key={p.id} className="sidebar-athlete-item" onClick={() => viewPlayer(p.athlete_user_id)}
            style={{ position: 'relative' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
              <input
                type="checkbox"
                checked={compareIds.has(p.athlete_user_id)}
                onClick={(e) => e.stopPropagation()}
                onChange={() => toggleCompare(p.athlete_user_id)}
                style={{ flexShrink: 0 }}
                aria-label={`Compare ${p.name}`}
              />
              {p.primary_photo && p.primary_photo.startsWith('https://') && (
                <img src={p.primary_photo} alt="" style={{
                  width: '32px', height: '32px', borderRadius: '50%',
                  objectFit: 'cover', flexShrink: 0, background: '#f1f5f9',
                }} />
              )}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                  <span style={{ fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flex: '0 1 auto' }}>
                    {p.name}
                  </span>
                  {p.has_new_activity && <NewActivityDot />}
                </div>
                <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                  {p.graduation_year ? `'${String(p.graduation_year).slice(-2)}` : ''} {(p.school ?? p.high_school) || ''}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
              {(() => {
                const stage = stageOf(p);
                const def = PIPELINE_STAGES.find(s => s.value === stage) || PIPELINE_STAGES[0];
                return (
                  <select
                    value={stage}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => { e.stopPropagation(); updateMemberStage(p.id, e.target.value); }}
                    title="Pipeline stage"
                    aria-label={`Pipeline stage for ${p.name}`}
                    style={{
                      fontSize: '10px', fontWeight: 700, padding: '2px 4px',
                      borderRadius: '8px', cursor: 'pointer',
                      color: def.color, background: def.bg,
                      border: `1px solid ${def.color}`,
                    }}
                  >
                    {PIPELINE_STAGES.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                );
              })()}
              {p.grade && <StarGrade value={p.grade} />}
              <Button
                iconOnly
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm(`Remove ${p.name} from this list?`)) {
                    axios.delete(`${API_URL}/coach-needs/${selectedListId}/board/${p.athlete_user_id}`, getAxiosConfig())
                      .then(() => { fetchListPlayers(selectedListId); fetchLists(); })
                      .catch(err => console.error('Delete error:', err));
                  }
                }}
                title="Remove from list"
                style={{ color: '#cbd5e1' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#cbd5e1'; }}
                aria-label={`Remove ${p.name}`}
              >
                &times;
              </Button>
            </div>
          </div>
        ))}

        {compareIds.size >= 2 && (
          <div style={{ padding: '12px' }}>
            <Button
              variant="primary"
              size="sm"
              fullWidth
              onClick={() => navigate(`/compare?ids=${[...compareIds].join(',')}`)}
            >
              Compare ({compareIds.size})
            </Button>
          </div>
        )}

        {selectedListId && listPlayers.length > 0 && (
          <div style={{ padding: '12px', borderTop: '1px solid #e2e8f0' }}>
            <Button
              variant="secondary"
              size="sm"
              fullWidth
              onClick={() => {
                const token = localStorage.getItem('token');
                fetch(`${API_URL}/coach-needs/${selectedListId}/export`, {
                  headers: { 'Authorization': `Bearer ${token}` }
                }).then(res => res.blob()).then(blob => {
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', 'ProZpects_List_Export.csv');
                  link.click();
                  URL.revokeObjectURL(url);
                }).catch(e => console.error('Export error:', e));
              }}
            >
              Download CSV
            </Button>
            <Button
              variant="primary"
              size="sm"
              fullWidth
              style={{ marginTop: '8px' }}
              onClick={() => {
                setBulkMessageText('');
                setBulkAttachmentFile(null);
                setBulkSendResult(null);
                setBulkConfirmStep(false);
                setShowMessageListModal(true);
              }}
            >
              Message List ({listPlayers.length})
            </Button>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <div className="coach-main">
        <PageHeader
          eyebrow={<MonoLabel>SEARCH · ATHLETES</MonoLabel>}
          title="Find your"
          accent="next prospect."
          sub="Filter the verified athlete pool by class, region, position, and stat thresholds."
        />


        {/* Search and Filters */}
        <div className="card" style={{ marginBottom: '24px' }}>
          {/* Search bar */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'stretch' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/>
                <path d="M16 16L20 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <input
                type="text"
                className="search-bar"
                style={{ paddingLeft: '42px' }}
                placeholder="Search by name, school, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchPlayers()}
                aria-label="Search athletes by name, school, or email"
              />
            </div>
            <Button variant="primary" onClick={searchPlayers} disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleSaveCurrentSearch}
              disabled={!hasActiveFilters}
              title={hasActiveFilters ? 'Save current filters as a search' : 'Add filters first'}
            >
              ☆ Save Search
            </Button>
          </div>

          {/* Filter rows — stacked vertically with AND between */}
          <div style={{ marginTop: '14px' }}>
            {activeStatIds.map((statId, idx) => {
              const def = findStatDef(statId);
              if (!def) return null;

              const chipEl = def.type === 'select' ? (() => {
                const val = filters[def.filterKey];
                return (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '0 4px 0 14px', fontSize: '14px',
                    border: '1.5px solid', borderRadius: '20px',
                    borderColor: val ? '#93b4f8' : '#e2e8f0',
                    background: val ? '#f0f4ff' : '#fff',
                    transition: 'all 0.15s',
                  }}>
                    <span style={{ color: '#475569', fontWeight: 500, whiteSpace: 'nowrap', fontSize: '13px' }}>{def.label}:</span>
                    <select
                      value={val}
                      onChange={(e) => setFilters(prev => ({ ...prev, [def.filterKey]: e.target.value }))}
                      style={{
                        border: 'none', background: 'transparent', fontSize: '14px',
                        color: '#1a202c', fontWeight: 600, padding: '8px 4px',
                        outline: 'none', cursor: 'pointer', maxWidth: '140px',
                      }}
                      aria-label={def.label}
                    >
                      <option value="">Any</option>
                      {def.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <Button iconOnly variant="ghost" size="sm" onClick={() => removeStat(statId)} aria-label={`Remove ${def.label} filter`}>&times;</Button>
                  </div>
                );
              })() : (() => {
                const minVal = filters[def.minKey];
                const maxVal = filters[def.maxKey];
                const hasValue = minVal || maxVal;
                return (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    padding: '0 4px 0 14px', fontSize: '14px',
                    border: '1.5px solid', borderRadius: '20px',
                    borderColor: hasValue ? '#93b4f8' : '#e2e8f0',
                    background: hasValue ? '#f0f4ff' : '#fff',
                    transition: 'all 0.15s',
                    flexWrap: 'wrap',
                  }}>
                    <span style={{ color: '#475569', fontWeight: 500, whiteSpace: 'nowrap', fontSize: '13px' }}>{def.label}:</span>
                    <input
                      type="number"
                      value={minVal}
                      onChange={(e) => setFilters(prev => ({ ...prev, [def.minKey]: e.target.value }))}
                      placeholder="min"
                      step={def.step || undefined}
                      style={{
                        border: 'none', background: 'transparent', fontSize: '14px',
                        color: '#1a202c', fontWeight: 600, padding: '8px 2px',
                        outline: 'none', width: '60px', textAlign: 'center',
                      }}
                      aria-label={`${def.label} minimum`}
                    />
                    <span style={{ color: '#94a3b8', fontSize: '12px' }}>to</span>
                    <input
                      type="number"
                      value={maxVal}
                      onChange={(e) => setFilters(prev => ({ ...prev, [def.maxKey]: e.target.value }))}
                      placeholder="max"
                      step={def.step || undefined}
                      style={{
                        border: 'none', background: 'transparent', fontSize: '14px',
                        color: '#1a202c', fontWeight: 600, padding: '8px 2px',
                        outline: 'none', width: '60px', textAlign: 'center',
                      }}
                      aria-label={`${def.label} maximum`}
                    />
                    <Button iconOnly variant="ghost" size="sm" onClick={() => removeStat(statId)} aria-label={`Remove ${def.label} filter`}>&times;</Button>
                    {STAT_PRESETS[def.id] && (
                      <div style={{ display: 'flex', gap: 6, marginLeft: 8, flexWrap: 'wrap' }}>
                        {STAT_PRESETS[def.id].values.map((v) => {
                          const preset = STAT_PRESETS[def.id];
                          const targetKey = preset.key === 'min' ? def.minKey : def.maxKey;
                          const otherKey = preset.key === 'min' ? def.maxKey : def.minKey;
                          const active = filters[targetKey] === String(v);
                          return (
                            <button
                              key={v}
                              type="button"
                              aria-pressed={active}
                              onClick={() =>
                                setFilters((prev) => ({
                                  ...prev,
                                  [targetKey]: active ? '' : String(v),
                                  ...(active ? {} : { [otherKey]: '' }),
                                }))
                              }
                              style={{
                                padding: '3px 10px', fontSize: 11, borderRadius: 12, cursor: 'pointer',
                                border: `1px solid ${active ? '#93b4f8' : '#e2e8f0'}`,
                                background: active ? '#f0f4ff' : '#fff',
                                color: active ? '#0066cc' : '#64748b',
                              }}
                            >
                              {preset.fmt(v)}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })();

              return (
                <div key={statId} style={{ marginBottom: '6px' }}>
                  {idx === 0 && (
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Filter by:
                    </div>
                  )}
                  {idx > 0 && (
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#1a202c', margin: '4px 0 6px 4px' }}>
                      AND
                    </div>
                  )}
                  {chipEl}
                </div>
              );
            })}

            {/* Add Filter — cascading menu */}
            <div style={{ marginTop: activeStatIds.length > 0 ? '4px' : '0' }}>
            {activeStatIds.length === 0 && (
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#1a202c', marginBottom: '6px' }}>
                Add Filter:
              </div>
            )}
            <div style={{ position: 'relative' }} ref={filterMenuRef}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => { setFilterMenuOpen(prev => !prev); setHoveredGroup(null); }}
                aria-label="Add a filter"
                aria-expanded={filterMenuOpen}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Add Filter
              </Button>

              {filterMenuOpen && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 50,
                  display: 'flex',
                }}>
                  {/* Level 1: Categories */}
                  <div style={{
                    background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)', width: '180px',
                    overflow: 'hidden',
                  }}>
                    <div style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>
                      Filter by
                    </div>
                    {STAT_MENU.map(g => {
                      const availableStats = g.stats.filter(s => !activeStatIds.includes(s.id));
                      if (availableStats.length === 0) return null;
                      return (
                        <Button
                          key={g.group}
                          variant="ghost"
                          size="sm"
                          onMouseEnter={() => setHoveredGroup(g.group)}
                          onClick={() => setHoveredGroup(g.group === hoveredGroup ? null : g.group)}
                          style={{
                            width: '100%', justifyContent: 'space-between', textAlign: 'left',
                            background: hoveredGroup === g.group ? '#f1f5f9' : undefined,
                            fontWeight: hoveredGroup === g.group ? 600 : undefined,
                          }}
                        >
                          {g.group}
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <polyline points="9 6 15 12 9 18"/>
                          </svg>
                        </Button>
                      );
                    })}
                  </div>

                  {/* Level 2: Stats in hovered category */}
                  {hoveredGroup && (
                    <div
                      style={{
                        background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px',
                        boxShadow: '0 8px 30px rgba(0,0,0,0.12)', width: '200px',
                        marginLeft: '4px', overflow: 'hidden',
                      }}
                      onMouseEnter={() => {}}
                    >
                      <div style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>
                        {hoveredGroup}
                      </div>
                      {STAT_MENU.find(g => g.group === hoveredGroup)?.stats
                        .filter(s => !activeStatIds.includes(s.id))
                        .map(s => (
                          <Button
                            key={s.id}
                            variant="ghost"
                            size="sm"
                            onClick={() => addStat(s.id)}
                            style={{ width: '100%', justifyContent: 'flex-start', textAlign: 'left' }}
                            onMouseEnter={(e) => { e.target.style.background = '#f1f5f9'; }}
                            onMouseLeave={(e) => { e.target.style.background = 'none'; }}
                          >
                            {s.label}
                            {s.type === 'range' && <span style={{ fontSize: '11px', color: '#94a3b8', marginLeft: '6px' }}>min/max</span>}
                          </Button>
                        ))
                      }
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Clear all */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                style={{ marginLeft: '8px' }}
              >
                Clear all
              </Button>
            )}
            </div>
          </div>

          {/* Verified + Has photo — single row */}
          <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            <label style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              cursor: 'pointer', fontSize: '14px', color: '#334155',
              padding: '6px 0',
            }}>
              <input
                type="checkbox"
                checked={filters.verified_only}
                onChange={(e) => setFilters(prev => ({ ...prev, verified_only: e.target.checked }))}
                style={{ width: '18px', height: '18px', accentColor: '#1d9bf0', cursor: 'pointer' }}
              />
              <VerifiedFlame />
              <span style={{ fontWeight: 500 }}>ProZpects Verified Athletes Only</span>
            </label>

            <label style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              cursor: 'pointer', fontSize: '14px', color: '#334155',
              padding: '6px 0',
            }}>
              <input
                type="checkbox"
                checked={filters.has_photo}
                onChange={(e) => setFilters(prev => ({ ...prev, has_photo: e.target.checked }))}
                style={{ width: '18px', height: '18px', accentColor: '#1d9bf0', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 500 }}>Has photo</span>
            </label>

            <label style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                fontSize: '14px', color: '#334155',
                padding: '6px 0',
              }}>
                <span style={{ fontWeight: 500 }}>Camp:</span>
                <select
                  value={filters.camp_id}
                  onChange={(e) => setFilters(prev => ({ ...prev, camp_id: e.target.value }))}
                  style={{
                    fontSize: '14px', color: '#1a202c', fontWeight: 500,
                    border: '1px solid #e2e8f0', borderRadius: '6px',
                    padding: '4px 8px', cursor: 'pointer', background: '#fff',
                  }}
                  aria-label="Filter by camp"
                >
                  <option value="">Any camp</option>
                  {campOptions.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </label>
          </div>

          {/* Results count */}
          {total > 0 && (
            <div style={{ marginTop: '14px', fontSize: '13px', color: '#64748b' }}>
              {total} athlete{total !== 1 ? 's' : ''} found
            </div>
          )}
        </div>

        {/* Results */}
        {players.length > 0 && (
          <div className="results-grid">
            {players.map((player) => (
              <div
                key={player.id}
                className="player-card"
                onClick={() => viewPlayer(player.user_id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') viewPlayer(player.user_id); }}
                aria-label={`View ${player.name}'s profile`}
              >
                {player.primary_photo && (
                  <img src={player.primary_photo} alt={player.name} className="player-card__photo" />
                )}
                <div className="player-card__body">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px', gap: '6px' }}>
                    <div className="player-card__name" style={{ margin: 0 }}>{player.name}</div>
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      {player.aid_fit && (
                        <span
                          title="Your program's athletic aid is a strong financial fit for this athlete"
                          style={{
                            fontSize: '11px', fontWeight: 600, padding: '2px 8px',
                            borderRadius: '10px', background: '#ecfdf5', color: '#047857',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Aid Fit
                        </span>
                      )}
                      {player.power_score != null && (
                        <span
                          className={`power-score-chip ${player.power_score >= 90 ? 'power-score-chip--high' : player.power_score >= 70 ? 'power-score-chip--mid' : 'power-score-chip--low'}`}
                          title={`ProZpects Power Score: ${player.power_score}`}
                        >
                          {player.power_score} PWR
                        </span>
                      )}
                      {player.position_primary && (
                        <span style={{
                          fontSize: '11px', fontWeight: 600, padding: '2px 8px',
                          borderRadius: '10px', background: '#eff6ff', color: '#4D8064',
                          whiteSpace: 'nowrap',
                        }}>
                          {player.position_primary}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="player-card__detail">
                    {player.school_logo && (
                      <img
                        src={`${API_URL}/image-proxy?url=${encodeURIComponent(player.school_logo)}`}
                        alt=""
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        style={{ width: '20px', height: '20px', objectFit: 'contain', verticalAlign: 'middle', marginRight: '4px', borderRadius: '3px' }}
                      />
                    )}
                    {(player.school ?? player.high_school) || 'N/A'}
                    {(player.high_school_city || player.high_school_state) ? `, ${[player.high_school_city, player.high_school_state].filter(Boolean).join(', ')}` : ''}
                  </div>
                  <div className="player-card__detail">Class of {player.graduation_year || 'N/A'}{player.city || player.state ? ` · ${[player.city, player.state].filter(Boolean).join(', ')}` : ''}</div>
                  <div className="player-card__detail">GPA: {player.gpa_weighted || 'N/A'} · {formatHeight(player.height_inches)}{player.weight_lbs ? ` · ${player.weight_lbs} lbs` : ''}</div>
                  {player.email && (
                    <div className="player-card__detail" style={{ wordBreak: 'break-all' }}>
                      <a
                        href={`mailto:${player.email}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: 'inherit' }}
                      >
                        {player.email}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Load more */}
        {players.length > 0 && players.length < total && (
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <Button
              variant="secondary"
              onClick={() => searchPlayers({ reset: false })}
              loading={loading}
              loadingText="Loading…"
            >
              {`Load more (${players.length} of ${total})`}
            </Button>
          </div>
        )}

        {players.length === 0 && !loading && (
          <div className="card">
            {hasActiveFilters ? (
              <EmptyState
                title="No athletes found"
                body="Try widening your search — fewer filters or a broader name."
              />
            ) : (
              <EmptyState
                title="Search for athletes"
                body="Enter a name or add a filter (sport, position, grad year, state…) to begin."
              />
            )}
          </div>
        )}
      </div>

      {/* Message List Modal */}
      {showMessageListModal && (
        <div className="modal-overlay" onClick={() => !bulkSending && setShowMessageListModal(false)}>
          {/* .modal-panel, not .modal-content — the latter is defined only in
              styles/index.css, which nothing imports, so it rendered the panel
              with no background. .modal-close is absolute against this panel. */}
          <div className="modal-panel" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <button className="modal-close" onClick={() => !bulkSending && setShowMessageListModal(false)}>&times;</button>
            <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 700, color: '#0f172a' }}>Message List</h2>
            <div>
              {bulkSendResult ? (
                <div>
                  <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                    <div style={{ fontSize: '48px', marginBottom: '8px' }}>{bulkSendResult.sent > 0 ? '\u2705' : '\u26A0\uFE0F'}</div>
                    <h3 style={{ margin: 0 }}>
                      {bulkSendResult.sent > 0 ? `Sent to ${bulkSendResult.sent} player${bulkSendResult.sent !== 1 ? 's' : ''}` : 'No messages sent'}
                    </h3>
                    {bulkSendResult.skipped > 0 && (
                      <p style={{ color: '#94a3b8', fontSize: '14px', marginTop: '8px' }}>
                        {bulkSendResult.skipped} skipped (blocked or unavailable)
                      </p>
                    )}
                  </div>
                  <Button
                    variant="primary"
                    fullWidth
                    onClick={() => setShowMessageListModal(false)}
                  >
                    Done
                  </Button>
                </div>
              ) : bulkConfirmStep ? (
                <div>
                  <p style={{ textAlign: 'center', fontSize: '16px', margin: '16px 0' }}>
                    Send this message to <strong>{listPlayers.length} player{listPlayers.length !== 1 ? 's' : ''}</strong>?
                  </p>
                  {bulkMessageText && (
                    <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px', marginBottom: '12px', fontSize: '14px', color: '#334155', whiteSpace: 'pre-wrap' }}>
                      {bulkMessageText}
                    </div>
                  )}
                  {bulkAttachmentFile && (
                    <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '8px 12px', marginBottom: '12px', fontSize: '13px', color: '#64748b' }}>
                      {bulkAttachmentFile.type.startsWith('image/') ? '\uD83D\uDDBC\uFE0F' : '\uD83C\uDFA5'} {bulkAttachmentFile.name}
                    </div>
                  )}
                  <p style={{ color: '#94a3b8', fontSize: '13px', textAlign: 'center', margin: '12px 0' }}>
                    Each player will receive this as an individual message. They cannot see other recipients.
                  </p>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <Button
                      variant="secondary"
                      style={{ flex: 1 }}
                      onClick={() => setBulkConfirmStep(false)}
                      disabled={bulkSending}
                    >
                      Back
                    </Button>
                    <Button
                      variant="primary"
                      style={{ flex: 1 }}
                      disabled={bulkSending}
                      onClick={async () => {
                        setBulkSending(true);
                        try {
                          const MESSAGING_URL = `${API_URL.replace('/api', '')}/messaging`;
                          const token = localStorage.getItem('token');
                          const headers = { 'Authorization': `Bearer ${token}` };

                          let attachmentUrl = null;
                          let attachmentType = null;

                          if (bulkAttachmentFile) {
                            const formData = new FormData();
                            formData.append('file', bulkAttachmentFile);
                            const uploadRes = await axios.post(`${MESSAGING_URL}/upload`, formData, {
                              headers: { ...headers, 'Content-Type': 'multipart/form-data' }
                            });
                            attachmentUrl = uploadRes.data.url;
                            attachmentType = uploadRes.data.type;
                          }

                          const userIds = listPlayers.map(p => p.athlete_user_id);
                          const res = await axios.post(`${MESSAGING_URL}/bulk-send`, {
                            user_ids: userIds,
                            content: bulkMessageText,
                            attachment_url: attachmentUrl,
                            attachment_type: attachmentType,
                          }, { headers: { ...headers, 'Content-Type': 'application/json' } });

                          setBulkSendResult(res.data);
                        } catch (e) {
                          console.error('Bulk send error:', e);
                          setBulkSendResult({ sent: 0, skipped: listPlayers.length, total: listPlayers.length });
                        }
                        setBulkSending(false);
                      }}
                    >
                      {bulkSending ? 'Sending...' : `Send to ${listPlayers.length}`}
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <textarea
                    value={bulkMessageText}
                    onChange={e => setBulkMessageText(e.target.value)}
                    placeholder="Type your message..."
                    style={{
                      width: '100%', minHeight: '120px', padding: '12px', borderRadius: '8px',
                      border: '1px solid #e2e8f0', fontSize: '14px', resize: 'vertical',
                      fontFamily: 'inherit', boxSizing: 'border-box'
                    }}
                  />
                  <div style={{ margin: '12px 0' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: '#64748b' }}>
                      <input
                        type="file"
                        accept="image/*,video/*"
                        style={{ display: 'none' }}
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file && file.size > 10 * 1024 * 1024) {
                            alert('File must be under 10 MB');
                            return;
                          }
                          setBulkAttachmentFile(file || null);
                        }}
                      />
                      {'\uD83D\uDCCE'} Attach photo or video
                    </label>
                    {bulkAttachmentFile && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', fontSize: '13px', color: '#334155' }}>
                        <span>{bulkAttachmentFile.name}</span>
                        <Button
                          iconOnly
                          variant="ghost"
                          size="sm"
                          onClick={() => setBulkAttachmentFile(null)}
                        >&times;</Button>
                      </div>
                    )}
                  </div>
                  <Button
                    variant="primary"
                    fullWidth
                    disabled={!bulkMessageText.trim() && !bulkAttachmentFile}
                    onClick={() => setBulkConfirmStep(true)}
                  >
                    Review & Send
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
